import { styled } from '@mui/material/styles';
import React from 'react';
import { useDispatch } from 'react-redux';
import { cacheActions } from 'redux/cache.slice';
import WsButton from 'stories/inputs/WsButton';

interface Crop {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Props {
  disabled: boolean;
  imageCount: number;
  maxImages: number;
  addImageFn: (blobs: Blob[]) => void;
  setBusyFn: (busy: boolean) => void;
}

const Input = styled('input')({
  display: 'none',
});

const targetAr = 4 / 3;

const ImageAdder = (props: Props) => {
  const dispatch = useDispatch();

  const cropImage = (image: HTMLImageElement) => {
    const crop: Crop = {
      x: 0,
      y: 0,
      width: image.width,
      height: image.height,
    };
    const imageAr = image.width / image.height;
    if (imageAr > targetAr) {
      for (let i = image.height; i >= 0; --i) {
        if ((i * targetAr) % 1 === 0) {
          crop.width = i * targetAr;
          crop.height = i;
          break;
        }
      }
    } else if (imageAr < targetAr) {
      for (let i = image.width; i >= 0; --i) {
        if ((i / targetAr) % 1 === 0) {
          crop.width = i;
          crop.height = i / targetAr;
          break;
        }
      }
    }
    crop.x = Math.floor((image.width - crop.width) / 2);
    crop.y = Math.floor((image.height - crop.height) / 2);
    // console.log('crop', crop);
    return crop;
  };

  const resizeToBlob = async (image: HTMLImageElement, crop: Crop): Promise<Blob | null> => {
    const canvas = document.createElement('canvas');
    const pixelRatio = window.devicePixelRatio;

    const imageScaleX = image.naturalWidth / image.width;
    const imageScaleY = image.naturalHeight / image.height;

    const canvasScaleX = 1024 / (crop.width * pixelRatio);
    const canvasScaleY = 768 / (crop.height * pixelRatio);

    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    canvas.width = canvasScaleX * crop.width * pixelRatio;
    canvas.height = canvasScaleY * crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * imageScaleX,
      crop.y * imageScaleY,
      crop.width * imageScaleX,
      crop.height * imageScaleY,
      0,
      0,
      crop.width * canvasScaleX,
      crop.height * canvasScaleY,
    );

    return new Promise(resolve => {
      canvas.toBlob(
        async (blob: Blob | null) => {
          // console.log('toblob', blob);
          resolve(blob);
        },
        'image/png',
        1,
      );
    });
  };

  const processImage = async (imageResult: string, filename: string): Promise<Blob | undefined> => {
    return new Promise(resolve => {
      try {
        const image = new Image();
        image.src = imageResult;
        image.onerror = () => {
          dispatch(
            cacheActions.addSnack({
              message: `Error loading '${filename}': Unsupported format`,
              variant: 'error',
            }),
          );
          resolve(undefined);
        };
        image.onload = async () => {
          if (image.width < 700 || image.height < 525) {
            dispatch(
              cacheActions.addSnack({
                message: `Error loading '${filename}': Images must be a minimum of 800 x 600 pixels`,
                variant: 'error',
              }),
            );
          } else {
            const crop = cropImage(image);
            const blob = await resizeToBlob(image, crop);
            if (blob) {
              resolve(blob);
            } else resolve(undefined);
          }
        };
      } catch (error) {
        resolve(undefined);
      }
    });
  };

  const readFile = async (file: File): Promise<string> => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.addEventListener('load', e => {
        resolve(e.target?.result as string);
      });
      reader.readAsDataURL(file);
    });
  };

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      props.setBusyFn(true);
      const imageCountToAdd =
        props.imageCount + event.target.files.length <= props.maxImages
          ? event.target.files.length
          : props.maxImages - props.imageCount;

      const blobs: Blob[] = [];
      for (let i = 0; i < imageCountToAdd; i++) {
        const data = await readFile(event.target.files[i]);
        const blob = await processImage(data, event.target.files[i].name);
        if (blob) blobs.push(blob);
      }
      if (props.imageCount + event.target.files.length > props.maxImages)
        dispatch(
          cacheActions.addSnack({
            message: `Could not load all files: Max image count exceeeded`,
            variant: 'warning',
          }),
        );
      if (blobs.length) props.addImageFn(blobs);
      else {
        console.log('here');
        props.setBusyFn(false);
      }
    }
  };

  return (
    <label htmlFor="contained-button-file">
      <Input
        accept="image/*"
        id="contained-button-file"
        type="file"
        multiple
        onClick={e => (e.currentTarget.value = '')}
        onChange={onFileSelected}
      />
      <WsButton label="Add Photos" disabled={props.disabled} color="secondary" component="span" />
    </label>
  );
};

export default ImageAdder;
